import { NestedTreeControl } from '@angular/cdk/tree';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { ApiLinks } from '@cutting/app.api-links';
import { LocalStorageManager } from '@cutting/main/shared/helper/local-storage-manager.helper';
import { ICustomResponse, PriceListProductGroup } from 'cloud-shared-lib';
import * as _ from 'lodash';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

import { AsyncPipe, NgClass, NgIf, NgTemplateOutlet } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatTreeModule } from '@angular/material/tree';
import { OrderDetailService } from '../../../order-detail.service';

@Component({
  selector: 'cutting-price-list-product-groups',
  templateUrl: './price-list-product-groups.component.html',
  styleUrls: ['./price-list-product-groups.component.scss'],
  standalone: true,
  imports: [NgIf, NgClass, NgTemplateOutlet, AsyncPipe, MatTreeModule, MatIconModule, MatButtonModule],
})
export class PriceListProductGroupsComponent implements OnChanges {
  @Input() activeNodeId = 0;
  @Input() priceListId = 0;
  @Output() selectedGroupChange: EventEmitter<number> = new EventEmitter();

  data$: Observable<ICustomResponse<PriceListProductGroup[]>>;

  nestedTreeControl: NestedTreeControl<PriceListProductGroup>;
  nestedDataSource: MatTreeNestedDataSource<PriceListProductGroup>;

  get userId(): number {
    return LocalStorageManager.regData;
  }

  constructor(private orderDetailService: OrderDetailService) {
    this.nestedTreeControl = new NestedTreeControl<PriceListProductGroup>(this.getChildren);
    this.nestedDataSource = new MatTreeNestedDataSource();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.priceListId?.currentValue) {
      this.data$ = this.orderDetailService.getPriceListProductGroup(this.priceListId).pipe(
        tap(g => ((this.nestedDataSource.data = g.result), (this.nestedTreeControl.dataNodes = g.result))),
        tap(g => {
          if (g.result && g.result.length > 0) {
            const group = this.findForActiveNode(g.result, this.activeNodeId);
            this.selectGroup(group ? group.id : g.result[0].id);
            if (group) {
              this.expandRootGroups(group);
            } else {
              this.nestedTreeControl.expand(this.nestedTreeControl.dataNodes[0]);
            }
          }
        })
      );
    }
  }

  hasNestedChild = (__: number, nodeData: PriceListProductGroup): boolean =>
    !_.isEmpty(nodeData.children) && !_.isNil(nodeData.children);

  selectGroup(nodeId: number): void {
    this.activeNodeId = nodeId;
    this.selectedGroupChange.emit(this.activeNodeId);
  }

  getGroupTexturePath(picturePath: string): string {
    if (this.userId === 0) {
      console.error('(c) Not enough parameters to get texture');
      return '';
    }
    return ApiLinks.elasticModelGroupTexture(picturePath, this.priceListId, this.userId);
  }

  expandRootGroups(node: PriceListProductGroup): void {
    if (node.parentId != null) {
      this.nestedTreeControl.expand(node);
      const parentNode = this.findForActiveNode(this.nestedDataSource.data, node.parentId);
      this.expandRootGroups(parentNode);
    } else {
      this.nestedTreeControl.expand(node);
    }
  }

  private findForActiveNode(nodes: PriceListProductGroup[], activeNodeId: number): PriceListProductGroup {
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      if (node.id === activeNodeId) {
        return node;
      }
      const childNodes = node.children;
      if (childNodes && childNodes.length > 0) {
        const result = this.findForActiveNode(childNodes, activeNodeId);
        if (result) {
          return result;
        }
      }
    }
    return null;
  }

  private getChildren = (node: PriceListProductGroup): Observable<PriceListProductGroup[]> => of(node.children);
}
